import { useCallback, useMemo } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { cx } from '@/utils/cx';

import styles from './BottomNavigation.module.scss';
import { ActionProvider } from './BottomNavigationContext';

export interface BottomNavigationProps {
  children: ReactNode;
  /** 모든 Action 클릭 시 호출되는 중앙 핸들러 */
  onAction?: (id: string, meta: Record<string, unknown>, originalEvent: React.MouseEvent) => void;
  /** 접근성 라벨 (기본값 '하단 메뉴') */
  'aria-label'?: string;
  /**
   * fixed bottom-nav 의 z-index. 미지정 시 디자인 시스템 default (30).
   * 명시 시 nav inline style 로 `--core-ui-bottom-nav-z-index` 변수에 inject — className 의 CSS 변수보다 우선.
   *
   * 정적 선언 / `calc()` / 디자인 토큰 `var()` 참조가 필요하면 CSS 변수 직접 선언 권장.
   */
  zIndex?: number;
  className?: string;
}

export const BottomNavigation = ({
  children,
  className,
  onAction,
  zIndex,
  'aria-label': ariaLabel = '하단 메뉴',
}: BottomNavigationProps) => {
  const handleAction = useCallback(
    (id: string, meta: Record<string, unknown>, e: React.MouseEvent) => {
      onAction?.(id, meta, e);
    },
    [onAction],
  );

  const actionValue = useMemo(() => ({ onAction: handleAction }), [handleAction]);

  const navStyle = useMemo<CSSProperties | undefined>(() => {
    if (zIndex === undefined) return undefined;
    return { '--core-ui-bottom-nav-z-index': String(zIndex) } as CSSProperties;
  }, [zIndex]);

  return (
    <ActionProvider value={actionValue}>
      <nav
        aria-label={ariaLabel}
        className={cx(styles.root, className)}
        data-core-ui-bottom-nav="root"
        style={navStyle}
      >
        <div className={styles.items}>{children}</div>
      </nav>
    </ActionProvider>
  );
};
